import { useState } from "react";
import { Link } from "react-router-dom";
import { Bed, Bath, Sofa, Car, Download, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { imageOrPlaceholder, imgErrorFallback } from "../../lib/placeholders.js";
import { downloadDesignBrochure } from "../../lib/brochure.js";

/**
 * Design listing card per PRD § 8.2. Hero image, name + Sq, icon row of
 * specs, "Fits lot WxD" line, brochure download + view details CTAs.
 */
export function DesignCard({ design }) {
  // Prefer the dedicated heroImage field over the gallery's first item.
  // A design with only a hero (and an empty gallery) used to fall through
  // to the placeholder pool, making every card look like stock filler.
  const heroImage = design?.heroImage || design?.images?.[0];
  const detailHref = `/home-designs/${design.slug}`;
  const fallbackKey = design.slug ?? design._id ?? design.name;
  const heroSrc = imageOrPlaceholder(heroImage, { pool: "design", key: fallbackKey });

  return (
    <article className="group flex flex-col overflow-hidden rounded-2xl border border-border-subtle bg-bg-elevated transition-all hover:-translate-y-0.5 hover:border-border hover:shadow-card-hover">
      <Link
        to={detailHref}
        className="relative aspect-[4/3] overflow-hidden"
        aria-label={`View ${design.name}`}
      >
        <img
          src={heroSrc}
          onError={imgErrorFallback(fallbackKey)}
          alt={design.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-bg-base/40 via-transparent to-transparent" />
        {design.isFeatured && (
          <span className="absolute left-4 top-4 rounded-full bg-accent px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-bg-base">
            Featured
          </span>
        )}
        {design.areaSq && (
          <span className="absolute right-4 top-4 rounded-full bg-bg-base/80 px-3 py-1 text-xs font-medium text-text-primary backdrop-blur">
            {design.areaSq} sq
          </span>
        )}
      </Link>

      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-xl text-text-primary">
              <Link to={detailHref} className="hover:text-accent">
                {design.name}
              </Link>
            </h3>
          </div>
        </div>

        {/* Spec icon row */}
        <ul className="mt-4 grid grid-cols-4 gap-2 text-xs text-text-secondary">
          <Spec icon={Bed} label={design.bedrooms} />
          <Spec icon={Bath} label={design.bathrooms} />
          <Spec icon={Sofa} label={design.livingAreas ?? 1} />
          <Spec icon={Car} label={design.garage ?? 0} />
        </ul>

        {design.lotFits && <p className="mt-4 text-xs text-text-muted">{design.lotFits}</p>}

        {/* Spec mini-table */}
        <dl className="mt-4 grid grid-cols-3 gap-2 rounded-md bg-bg-elevated-2 px-3 py-2 text-xs">
          <SpecCell label="Area" value={design.areaSqm ? `${design.areaSqm} m²` : "—"} />
          <SpecCell label="Length" value={design.length ? `${design.length} m` : "—"} />
          <SpecCell label="Width" value={design.width ? `${design.width} m` : "—"} />
        </dl>

        {/* CTA row — brochure + view details, side by side */}
        <div className="mt-5 grid grid-cols-2 gap-2">
          <BrochureButton design={design} />
          <Link
            to={detailHref}
            className="inline-flex items-center justify-center gap-1.5 rounded-md bg-accent px-3 py-2 text-sm font-medium text-bg-base transition-colors hover:bg-accent-hover"
          >
            View details
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </article>
  );
}

/**
 * Always-visible brochure button. Downloads the uploaded PDF if one exists,
 * otherwise generates a styled brochure on the fly from the design's spec
 * data so the visitor never gets a dead button.
 */
export function BrochureButton({ design, className = "" }) {
  const [busy, setBusy] = useState(false);

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      await downloadDesignBrochure(design);
    } catch (err) {
      toast.error("Couldn't generate the brochure — please try again.");
      console.error(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      className={`inline-flex items-center justify-center gap-1.5 rounded-md border border-border bg-bg-elevated-2 px-3 py-2 text-sm font-medium text-text-primary transition-colors hover:border-accent hover:text-accent disabled:opacity-60 ${className}`}
      aria-label={`Download ${design.name} brochure`}
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
      {busy ? "Preparing…" : "Download brochure"}
    </button>
  );
}

function Spec({ icon: Icon, label }) {
  return (
    <li className="flex items-center gap-1.5">
      <Icon className="h-3.5 w-3.5 text-accent" />
      <span>{label ?? 0}</span>
    </li>
  );
}

function SpecCell({ label, value }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wider text-text-muted">{label}</dt>
      <dd className="mt-0.5 text-text-primary">{value}</dd>
    </div>
  );
}

/* Skeleton card for loading state */
export function DesignCardSkeleton() {
  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border border-border-subtle bg-bg-elevated">
      <div className="aspect-[4/3] animate-pulse bg-bg-elevated-2" />
      <div className="space-y-3 p-5">
        <div className="h-5 w-3/4 animate-pulse rounded bg-bg-elevated-2" />
        <div className="h-3 w-1/2 animate-pulse rounded bg-bg-elevated-2" />
        <div className="h-12 animate-pulse rounded bg-bg-elevated-2" />
      </div>
    </div>
  );
}